import React, { ReactElement } from "react";
import styled from "styled-components";
import {
  ToastContainer as ReactToastifyToastContainer,
  ToastContainerProps,
  ToastOptions,
  TypeOptions as TypeOptionsToastify,
  toast,
} from "react-toastify";

import {
  DESTROY_NOTIFICATIONS_AFTER_LONG,
  MOBILE_TOAST_OFFSET,
} from "@config/consts";

import { TestWrapper } from "@ui/Atoms";

import { useScrollPosition } from "@hooks/useScrollPosition";
import { useBreakpoint } from "@hooks/useBreakpoint";

import { ToastifyCloseButton } from "./ToastifyCloseButton";

export type TypeOptions = TypeOptionsToastify;

export const showToast = (
  content: string | React.ReactElement,
  { type, ...opts }: ToastOptions,
): void => {
  if (opts.toastId) {
    toast(
      <TestWrapper testId={String(opts.toastId)}>
        <span>{content}</span>
      </TestWrapper>,
      { ...opts, type: type ?? "default" },
    );
    return;
  }

  toast(<span>{content}</span>, { ...opts, type: type ?? "default" });
};

export const hideToast = (id?: string): void => {
  toast.dismiss(id);
};

export const GenericErrorToastFactory =
  (fallbackMessage: string, toastId?: string) =>
  (customMessage?: string | null): void => {
    const message = customMessage || fallbackMessage;

    showToast(message, {
      type: "error",
      toastId,
    });
  };

export const ToastContainer = (props: ToastContainerProps): ReactElement => {
  const { mobile } = useBreakpoint("v1");
  const { currPos } = useScrollPosition();

  return (
    <StyledContainer stickToTop={Math.abs(currPos.y) >= MOBILE_TOAST_OFFSET}>
      <ReactToastifyToastContainer
        toastClassName={"Toastify__toast-body"}
        position={mobile ? "top-center" : "top-right"}
        hideProgressBar
        closeOnClick
        icon={false}
        closeButton={ToastifyCloseButton}
        autoClose={DESTROY_NOTIFICATIONS_AFTER_LONG}
        theme="colored"
        {...props}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ stickToTop: boolean }>`
  .Toastify__toast-container {
    transition: all 0.2s;
    top: ${props => (props.stickToTop ? 0 : props.theme.margins.base_x7)};
    color: ${props => props.theme.palette.black};

    ${props => props.theme.breakpoints.tablet} {
      top: ${props => props.theme.margins.base_x10};
    }
  }

  .Toastify__toast {
    padding: ${props => props.theme.spacing.spacing_16};
    border-radius: ${props => props.theme.buttons.borderRadius};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .Toastify__toast--default {
    background: #fff;
  }

  .Toastify__toast-theme--dark {
    background: ${props => props.theme.palette.blue900};
  }

  .Toastify__toast--info {
    background: #3498db;
  }

  .Toastify__toast--warning {
    background: #f1c40f;
  }

  .Toastify__toast--success {
    background: ${props => props.theme.palette.green500};
  }

  .Toastify__toast--error {
    background: ${props => props.theme.palette.red500};
  }

  .Toastify__close-button {
    top: ${props => props.theme.spacing.spacing_16};
    right: ${props => props.theme.spacing.spacing_16};
    display: flex;
    color: currentColor;

    & > svg {
      fill: currentColor;
      height: 16px;
      width: 14px;
    }
  }
`;
